// Heckles for the Heckle Responses exercise
// Random audience interruptions to practice comebacks
// Uses seeded shuffle to ensure no repeats until full list is cycled

/**
 * Seeded pseudo-random number generator (mulberry32).
 * Returns a function that produces deterministic floats in [0, 1) for a given seed.
 */
function seededRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Returns a shuffled copy of the heckles using a deterministic seed.
 * Same seed always produces the same order.
 */
function shuffleHeckles(seed: number): string[] {
  const hecks = [...HECKLES];
  const rand = seededRng(seed);
  for (let i = hecks.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [hecks[i], hecks[j]] = [hecks[j], hecks[i]];
  }
  return hecks;
}

/**
 * Get a deterministic heckle based on the current cycle.
 * Each user globally sees the same heckle for the same day,
 * and heckles never repeat until the full list is cycled.
 */
export function getRandomHeckle(daysSinceEpoch: number = 0): string {
  const totalHeckles = HECKLES.length;
  const cycle = Math.floor(Math.abs(daysSinceEpoch) / totalHeckles);
  const positionInCycle = Math.abs(daysSinceEpoch) % totalHeckles;
  const shuffled = shuffleHeckles(cycle + 1);
  return shuffled[positionInCycle];
}

export const HECKLES: string[] = [
  "You suck!",
  "Is that the punchline? Where's the punchline?",
  "My grandmother's funnier than you.",
  "You're not funny.",
  "Get off the stage.",
  "That joke sucks!",
  "You stole that from someone else.",
  "I've heard this before.",
  "You're bombing, dude.",
  "This is terrible.",
  "Speak up, we can't hear you!",
  "You're wasting our time.",
  "That's not even a joke.",
  "Can we get a REAL comic up here?",
  "My kid could do better than that.",
  "Did you write these jokes yourself? Because they suck.",
  "Is this your first time doing stand-up?",
  "You should give your money back.",
  "I paid good money for this.",
  "You're ruining my night.",
  "Has anyone ever laughed at these?",
  "Your delivery is terrible.",
  "The joke's funny, but you're not.",
  "Who let you on stage?",
  "I've seen better jokes on a cereal box.",
  "Are you even trying?",
  "That was painfully unfunny.",
  "You're making me uncomfortable.",
  "Can we skip to the next guy?",
  "How much did they pay you to not be funny?",
  "I thought this was supposed to be comedy.",
  "You're giving comedy a bad name.",
  "That didn't land.",
  "Nobody's laughing with you.",
  "You look nervous.",
  "I think you're in the wrong profession.",
  "How long is this going to go on?",
  "Do you have any actual jokes?",
  "I could do better than you, and I'm drunk.",
  "Why are you talking about your feelings? Tell a joke.",
];
